/**
 * Page Context and Provider
 *
 * This module provides React Context for managing Page state throughout the Auriga application.
 * It handles fetching available Page level data across grains, tracking the current Page in view,
 * and automatically updating the current Page based on route parameters.
 *
 */

import { DEFAULT_PAGE_DRIVERS, KPI_DIMENSION_CONFIG_MAP } from '@constants/page';
import { useDeckContext } from '@context/DeckContext';
import { useUserContext } from '@context/UserContext';
import {
  CodeOrDescription,
  DeckColumn,
  DenominatorLabelValues,
  KPIDrivers,
  Page,
} from '@services/api/deckManagement/graphql/schema/API';
import { useGetDeckColumns } from '@services/api/deckManagement/hooks';
import { useGetPagesByPageName } from '@services/api/deckManagement/hooks/usePage';
import { entityKeyAsId } from '@utils/deckHelpers.ts';
import { changeEntryField } from '@utils/deckManagementEntityHelpers';
import { generateDriverDisplayOptions, kpiDriversEmpty } from '@utils/kpiDriverHelpers';
import { initializePageModifiableProperties } from '@utils/pageHelpers';
import { cloneDeep, isEmpty, isNil, some, uniq } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LockStatus } from 'types/DeckContext.types';
import { DecklineRowData, PartialKPIDrivers } from 'types/Page.types';
import {
  DriverDisplayOptionMap,
  PageContextType,
  PageModifiableProperties,
  PageProviderProps,
} from 'types/PageContext.types';
import { KpiDimension } from 'types/PageSidePanel.types';

/**
 * Page Context
 *
 * React context for Page state management.
 */
export const PageContext = createContext<PageContextType>({
  availablePagesByName: [],
  availablePageGrains: [],
  currentCorePageName: null,
  currentGranularityPage: null,
  currentPageDriverOptions: null,
  updatePageDriverActiveState: () => {},
  setCurrentPageKPIDrivers: () => {},
  pageDeckColumns: [],
  selectedGranularity: null,
  segmentsDisplayMode: CodeOrDescription.CODE,
  setSelectedGranularity: () => {},
  setSegmentsDisplayMode: () => {},
  pageRowData: [],
  setPageRowData: () => {},
  isLoading: false,
  isError: false,
  error: null,
  setDenominatorLabel: () => {},
  modifiedPageProperties: null,
  pageLockStatus: { isLocked: false },
});

/**
 * Page Provider Component
 *
 * This component provides Page context to the application.
 * It manages the state of available Page Grains, the current Page selection, and all Pages associated with a page Name.
 *
 * @param {PageProviderProps} props - The component props for the Page Provider
 * @returns {JSX.Element} The Page provider component
 */
export const PageContextProvider = ({ children }: PageProviderProps): ReactNode => {
  const { currentDeckId } = useDeckContext();
  const { username } = useUserContext();
  // Get pageName from route parameters
  const { pageName: routePageName } = useParams<{ pageName: string }>();
  const pageName = decodeURIComponent(routePageName || '');

  // State for current page and error handling
  const [availablePagesByName, setAvailablePagesByName] = useState<Page[]>([]);
  const [availablePageGrains, setAvailablePageGrains] = useState<string[]>([]);
  const [currentCorePageName, setCurrentCorePageName] = useState<string | null>(null);
  const [currentGranularityPage, setCurrentGranularityPageState] = useState<Page | null>(null);
  const [currentOriginalPageKPIDrivers, setCurrentOriginalPageKPIDriversState] = useState<KPIDrivers | null>(null);
  const [currentPageDriverOptions, setCurrentPageDriverOptionsState] = useState<DriverDisplayOptionMap | null>(null);
  const [segmentsDisplayMode, setSegmentsDisplayMode] = useState<CodeOrDescription>(CodeOrDescription.CODE);
  const [pageDeckColumns, setPageDeckColumns] = useState<DeckColumn[]>([]);
  const [selectedGranularity, setSelectedGranularity] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageRowData, setPageRowData] = useState<DecklineRowData[]>([]);
  const [modifiedPageProperties, setModifiedPageProperties] = useState<PageModifiableProperties | null>(null);

  // Fetch associated Pages based on the current page's page_name
  const {
    data: pagesByName,
    isLoading: isPagesByNameLoading,
    isError: isPagesByNameError,
    error: pagesByNameError,
  } = useGetPagesByPageName({
    page_name: pageName,
    deck_id: currentDeckId, // To filter API results by loaded Deck
    options: {
      // Only fetch when we have a valid pageName and deckId
      enabled: !isNil(pageName) && !isEmpty(pageName) && !isNil(currentDeckId) && !isEmpty(currentDeckId),
    },
  });

  // Fetch current Page grain DeckColumns information
  const {
    data: deckColumns,
    isLoading: isDeckColumnsLoading,
    isError: isDeckColumnsError,
    error: deckColumnsError,
  } = useGetDeckColumns({
    pageId: currentGranularityPage?.pk ? entityKeyAsId(currentGranularityPage.pk) : '',
    options: {
      enabled: Boolean(currentDeckId && currentGranularityPage && !isNil(selectedGranularity)),
    },
  });

  /**
   * Resets any page data as needed to restore an empty context state
   * @param {boolean} omitPageNameReset - Whether to omit resetting the page name
   *
   */
  const resetPageData = useCallback((omitPageNameReset: boolean = false) => {
    if (!omitPageNameReset) {
      setCurrentCorePageName(null);
    }
    setAvailablePagesByName([]);
    setAvailablePageGrains([]);
    setCurrentGranularityPageState(null);
    setCurrentPageDriverOptionsState(null);
    setPageDeckColumns([]);
    setSelectedGranularity(null);
    setModifiedPageProperties(null);
  }, []);

  /**
   * Effect to automatically set current Page based on a change in the Route parameter
   *
   * This effect runs when the pageName route parameter changes and attempts
   * to set the first available granularity Page accordingly.
   */
  useEffect(() => {
    if (!isNil(pageName) && !isEmpty(pageName)) {
      // Reset any old values that will get updated
      resetPageData(true);
      // Assign the new Page name as the core Page name
      setCurrentCorePageName(pageName);
    } else {
      // Clear all page data if no pageName in route
      resetPageData();
    }
    setPageError(null);
  }, [pageName, resetPageData]);

  /**
   * Effect to automatically update available Page data based on the current Page selection by a User
   *
   * This effect runs when the currentCorePageName changes and updates the available Pages, Page Grains or Page Types,
   * and Page DeckColumns based on the fetched API data. It also updates the selected granularity if it has changed.
   */
  useEffect(() => {
    if (currentCorePageName) {
      if (pagesByName && pagesByName.length > 0) {
        // Updates PagesByName, AvailablePageGrains, and Granularity selection only when there is a match for the current Page selected
        const pagesMatchPageSelection = some(pagesByName, (page) => page.page_name?.startsWith(currentCorePageName));
        if (!pagesMatchPageSelection) {
          console.error('PagesByName does not match current page selection. Skipping stale data...');
        } else {
          setAvailablePagesByName(pagesByName);
          // Clear any previous errors when data loads successfully
          setPageError(null);

          // Additionally, update the page grains we account for and filter for current deck.
          // This also treats Types as Grains for the frontend, in case grain does not exist.
          const updatedPageGrains: string[] = pagesByName
            .filter((page) => (!isNil(page.page_grain) || !isNil(page.page_type)) && page.deck_id === currentDeckId)
            .map((page) => {
              if (!isEmpty(page.page_grain)) return page.page_grain as string;
              return page.page_type as string;
            })
            .sort();
          if (updatedPageGrains && updatedPageGrains.length > 0) {
            setAvailablePageGrains(uniq(updatedPageGrains));
            // Set the initial granularity to the first one available if they User does not have a Selected granularity,
            // or the current selected granularity does not match what is available
            if (!selectedGranularity || !updatedPageGrains.includes(selectedGranularity)) {
              setSelectedGranularity(updatedPageGrains[0]);
            }
          }
        }
      }
      if (deckColumns && deckColumns.length > 0) {
        // Updates DeckColumns only when there is a match for the current Page granularity selected
        const deckColumnsMatchPageSelection = some(
          deckColumns,
          (deckColumn) => deckColumn?.pk === currentGranularityPage?.pk,
        );
        if (!deckColumnsMatchPageSelection) {
          // Reset DeckColumns state
          console.debug('DeckColumns does not match current granularity page selection. Skipping stale data...');
          setPageDeckColumns([]);
        } else {
          // Set page granularity Deck Columns to State
          setPageDeckColumns(deckColumns as DeckColumn[]);
        }
      }
    }
  }, [currentCorePageName, pagesByName, deckColumns, selectedGranularity, currentGranularityPage?.pk, currentDeckId]);

  /**
   * Updates Page Driver Options
   * @param updates The changed values which need to be assigned to the KPIDrivers
   * @param decklineUpdateHandler - When provided, gets called with the previous state as well as the update object.
   *  Used for iterating through decklines and updating those values to reflect the page drivers.
   */
  const setCurrentPageKPIDrivers = useCallback(
    (updates: PartialKPIDrivers, decklineUpdateHandler?: (prev: KPIDrivers, updates: PartialKPIDrivers) => void) => {
      setModifiedPageProperties((prev) => {
        if (isNil(prev)) {
          return prev;
        }

        const newDrivers = {
          ...prev.page_drivers,
          ...updates,
        } as KPIDrivers;

        // When decklineUpdateHandler is provided, call it with the prev state and updates.
        decklineUpdateHandler?.(cloneDeep(prev.page_drivers!), updates);
        setCurrentPageDriverOptionsState(generateDriverDisplayOptions(newDrivers));
        return changeEntryField(prev, 'page_drivers', newDrivers);
      });
    },
    [],
  );

  /**
   * Effect to initialize Page Drivers with defaults if none exist
   */
  useEffect(() => {
    if (!isNil(modifiedPageProperties) && kpiDriversEmpty(modifiedPageProperties.page_drivers)) {
      setCurrentPageKPIDrivers(DEFAULT_PAGE_DRIVERS);
    }
  }, [modifiedPageProperties, setCurrentPageKPIDrivers]);

  /**
   * Updates the Denominator Label for the Current Page.
   * @param newValue - The new value for the Denominator Label
   */
  const setDenominatorLabel = useCallback((newValue: DenominatorLabelValues | null) => {
    setModifiedPageProperties((prev) => {
      if (isNil(prev)) {
        return prev;
      }

      return changeEntryField(prev, 'denominator_label', newValue);
    });
  }, []);

  /**
   * Updates the value of `active` for a given kpiDimension (when the user toggled the <Switch> component). When
   * deactivating a switch, the associated value and hierarchy are reset to `null`.
   * @param kpiDimension - The kpiDimension to update
   * @param active - The new value of `active`
   * @param decklineUpdateHandler - When provided, gets called with the previous state as well as the update object.
   *  Used for iterating through decklines and updating those values to reflect the page drivers.
   */
  const updatePageDriverActiveState = useCallback(
    (
      kpiDimension: KpiDimension,
      active: boolean,
      decklineUpdateHandler?: (prev: KPIDrivers, updates: PartialKPIDrivers) => void,
    ) => {
      const dimensionConfig = KPI_DIMENSION_CONFIG_MAP[kpiDimension];
      if (!dimensionConfig) {
        return;
      }

      const { decklineDrivers } = dimensionConfig;
      if (!decklineDrivers) {
        return;
      }

      setCurrentPageDriverOptionsState((prev) => {
        const newDriverOptions = { ...prev };
        if (newDriverOptions[kpiDimension]) {
          newDriverOptions[kpiDimension].active = active;

          // If deactivating a switch, we need to reset the values associated with the kpiDimension to `null`
          if (!active) {
            newDriverOptions[kpiDimension].value = null;
            newDriverOptions[kpiDimension].hierarchy = null;
          } else {
            // When activating, pre-populate with the original KPI Driver Data (if available).
            newDriverOptions[kpiDimension].value =
              currentOriginalPageKPIDrivers?.[decklineDrivers.valueAccessor!] || null;
            if (decklineDrivers.hierarchyAccessor) {
              newDriverOptions[kpiDimension].hierarchy =
                currentOriginalPageKPIDrivers?.[decklineDrivers.hierarchyAccessor] || null;
            }
          }

          // Keep currentPageKPIDrivers synchronized
          const resetDrivers: PartialKPIDrivers = {
            [decklineDrivers.valueAccessor!]: newDriverOptions[kpiDimension].value,
          };
          if (decklineDrivers?.hierarchyAccessor) {
            resetDrivers[decklineDrivers.hierarchyAccessor as keyof PartialKPIDrivers] =
              newDriverOptions[kpiDimension].hierarchy;
          }
          if (!isNil(modifiedPageProperties?.page_drivers)) {
            const prevDrivers = modifiedPageProperties.page_drivers;
            // Used to pre-populate Decklines with previous values from the Page Driver state.
            // This helps to not require the user to enter a value on every deckline.
            const decklineEntryUpdates: PartialKPIDrivers = {
              [decklineDrivers.valueAccessor!]: prevDrivers[decklineDrivers.valueAccessor!],
            };
            if (decklineDrivers.hierarchyAccessor) {
              decklineEntryUpdates[decklineDrivers.hierarchyAccessor as keyof PartialKPIDrivers] =
                prevDrivers[decklineDrivers.hierarchyAccessor];
            }

            setCurrentPageKPIDrivers({ ...prevDrivers, ...resetDrivers });
            // When decklineUpdateHandler is provided, call it with the prev state and updates.
            decklineUpdateHandler?.(cloneDeep(prevDrivers), decklineEntryUpdates);
          }
        }
        return newDriverOptions;
      });
    },
    [currentOriginalPageKPIDrivers, modifiedPageProperties, setCurrentPageKPIDrivers],
  );

  /**
   * Set current Page data by Granularity
   *
   * Attempts to find and set the current Page by the User selected granularity. It searches
   * in the availablePagesByName array for the desired Page data
   *
   * This is the primary way to change which Page is being viewed/edited.
   * This function is idempotent - calling it multiple times with the same granularity selection
   * will not trigger additional API calls or unnecessary re-renders.
   *
   * @param {string} targetGranularity - The ID of the Page to set as current
   */
  const setCurrentGranularityPage = useCallback(
    (targetGranularity: string) => {
      setPageError(null);

      // Only update selectedGranularity Page if it is actually different (considers both Grain and Type--where grain did not exist)
      if (
        currentGranularityPage &&
        (currentGranularityPage.page_grain === targetGranularity ||
          currentGranularityPage.page_type === targetGranularity)
      ) {
        console.debug('Page with granularity already selected, skipping:', targetGranularity);
        return;
      }

      // Return granular page if found in availablePagesByName
      const foundPage = availablePagesByName.find((page) => {
        return (page.page_grain === targetGranularity || page.page_type === targetGranularity) && page.page_name;
      });

      if (foundPage) {
        const drivers = kpiDriversEmpty(foundPage.page_drivers) ? DEFAULT_PAGE_DRIVERS : foundPage.page_drivers;
        setCurrentGranularityPageState(foundPage);
        setCurrentOriginalPageKPIDriversState(drivers || null);
        setCurrentPageDriverOptionsState(generateDriverDisplayOptions(drivers));
        setModifiedPageProperties(initializePageModifiableProperties(foundPage));
      } else if (!availablePagesByName.length && !isPagesByNameLoading) {
        // If no pages by name are available with the granularity, and we're not loading, set error
        setPageError(`Page data for granularity "${targetGranularity}" could not be found.`);
        setCurrentGranularityPageState(null);
        setCurrentPageDriverOptionsState(null);
        setCurrentOriginalPageKPIDriversState(null);
        setModifiedPageProperties(null);
      }
    },
    [availablePagesByName, currentGranularityPage, isPagesByNameLoading],
  );

  /**
   * Effect to automatically set correct Page data when selected granularity changes
   */
  useEffect(() => {
    if (selectedGranularity) {
      setCurrentGranularityPage(selectedGranularity);
    }
  }, [selectedGranularity, setCurrentGranularityPage]);

  /**
   * Keep currentGranularityPage in sync with freshly-fetched page data.
   *
   * A page lock is applied to ALL grains of the logical page at once, so locking/releasing
   * (or a subscription-driven refetch) changes lock attributes (locked_by/locked_at/
   * lock_expiry) on the currently-viewed grain's record without the user changing grain.
   * Since setCurrentGranularityPage short-circuits when the grain is unchanged, it does not
   * refresh the object after a refetch — so re-sync the current grain's object by pk whenever
   * pagesByName updates, so consumers (e.g. the page-lock button/banner) see live lock state.
   */
  useEffect(() => {
    if (!currentGranularityPage || !pagesByName?.length) {
      return;
    }
    const refreshedPage = pagesByName.find((page) => page.pk === currentGranularityPage.pk);
    if (refreshedPage && refreshedPage !== currentGranularityPage) {
      setCurrentGranularityPageState(refreshedPage);
    }
  }, [pagesByName, currentGranularityPage]);

  /**
   * Effect to clear Page error when Pages By Name is successfully loaded
   */
  useEffect(() => {
    if (availablePagesByName.length > 0 && pageError) {
      setPageError(null);
    }
  }, [availablePagesByName, pageError]);

  /**
   * Effect to handle API GetPagesByPageName fetch errors
   */
  useEffect(() => {
    if (isPagesByNameError && pageName) {
      setPageError(`Page with Name "${pageName}" has no page results.`);
      setCurrentGranularityPageState(null);
      setCurrentPageDriverOptionsState(null);
      setCurrentCorePageName(null);
      setModifiedPageProperties(null);
    } else if (!isPagesByNameError && pageError) {
      // Clear error when API error state changes from error to success
      setPageError(null);
    }
  }, [isPagesByNameError, pageName, pageError]);

  // Combine loading states
  const isLoading = isPagesByNameLoading || isDeckColumnsLoading;

  // Combine error states
  const isError = isPagesByNameError || isDeckColumnsError || !!pageError;
  const error =
    pageError || (isPagesByNameError ? pagesByNameError : null) || (isDeckColumnsError ? deckColumnsError : null);

  /**
   * Creates the Page context value object
   *
   * Combines all state variables and methods into a single object that will be
   * provided to consumers of the context. This ensures that the context value
   * only changes when one of its dependencies changes, preventing unnecessary
   * re-renders of components that consume the context.
   */
  /**
   * Per-page lock state derived from the current grain page record (all grains of a
   * logical page share the same lock attributes). Editing a page requires holding THIS
   * page's lock — independent of the deck lock, which gates publish / Page Library.
   */
  const pageLockStatus = useMemo<LockStatus>(() => {
    const lockedBy = currentGranularityPage?.locked_by ?? undefined;
    const isLocked = Boolean(lockedBy);
    const isLockedByCurrentUser = isLocked && lockedBy === username;
    return {
      isLocked,
      lockedBy,
      lockedAt: currentGranularityPage?.locked_at ?? undefined,
      lockExpiry: currentGranularityPage?.lock_expiry ?? undefined,
      isLockedByCurrentUser,
      // Editing is disabled unless the current user holds this page's lock.
      isEditingDisabled: !isLockedByCurrentUser,
    };
  }, [currentGranularityPage, username]);

  const contextValue = useMemo<PageContextType>(
    () => ({
      availablePagesByName,
      availablePageGrains,
      currentCorePageName,
      currentGranularityPage,
      currentPageDriverOptions,
      updatePageDriverActiveState,
      setCurrentPageKPIDrivers,
      pageDeckColumns,
      selectedGranularity,
      setSelectedGranularity,
      segmentsDisplayMode,
      setSegmentsDisplayMode,
      pageRowData,
      setPageRowData,
      isLoading,
      isError,
      error: error as Error | null,
      modifiedPageProperties,
      setDenominatorLabel,
      pageLockStatus,
    }),
    [
      availablePagesByName,
      availablePageGrains,
      currentCorePageName,
      currentGranularityPage,
      currentPageDriverOptions,
      updatePageDriverActiveState,
      modifiedPageProperties,
      setCurrentPageKPIDrivers,
      pageDeckColumns,
      selectedGranularity,
      segmentsDisplayMode,
      pageRowData,
      isLoading,
      isError,
      error,
      setDenominatorLabel,
      pageLockStatus,
    ],
  );

  return <PageContext value={contextValue}>{children}</PageContext>;
};

/**
 * Custom hook to access the Page context
 *
 * @returns {PageContextType} The deck context value
 */
export const usePageContext = (): PageContextType => {
  return use(PageContext);
};
